import { useState, useCallback, useEffect, useRef } from "react";
import { SideMenuPage, UseSideMenuResult } from "@/shared/type/SideMenu.type";
import { getSideMenu } from "../services/common/commonServices";
import { showToastDynamic } from "@/shared/utils/toast.util";

let preloadedMenuItems: SideMenuPage[] | null = null;
let preloadSideMenuRequest: Promise<SideMenuPage[] | null> | null = null;

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  return "";
};

const shouldSuppressMenuError = (error?: unknown): boolean => {
  const errorMessage = getErrorMessage(error).toLowerCase();

  return (
    errorMessage.includes("redirecting to login") ||
    errorMessage.includes("token acquisition failed") ||
    errorMessage.includes("interaction_in_progress")
  );
};

export const preloadSideMenu = async (): Promise<void> => {
  if (preloadedMenuItems) {
    return;
  }

  if (!preloadSideMenuRequest) {
    preloadSideMenuRequest = (async (): Promise<SideMenuPage[] | null> => {
      try {
        const result = await getSideMenu();
        if (result.success) {
          preloadedMenuItems = result.data.sideMenuPages;
          return preloadedMenuItems;
        }

        return null;
      } catch {
        return null;
      } finally {
        preloadSideMenuRequest = null;
      }
    })();
  }

  await preloadSideMenuRequest;
};

export const useSideMenu = (): UseSideMenuResult => {
  const [menuItems, setMenuItems] = useState<SideMenuPage[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const didInit = useRef(false);

  const fetchSideMenu = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(false);

      if (preloadedMenuItems) {
        setMenuItems(preloadedMenuItems);
        return;
      }

      if (preloadSideMenuRequest) {
        const preloadResult = await preloadSideMenuRequest;
        if (preloadResult) {
          setMenuItems(preloadResult);
          return;
        }
      }

      const result = await getSideMenu();
      if (result.success) {
        setMenuItems(result.data.sideMenuPages);
      } else {
        if (shouldSuppressMenuError()) {
          return;
        }

        setError(true);
        showToastDynamic(
          result.message ?? "Failed to load navigation menu.",
          "error",
        );
      }
    } catch (caughtError: unknown) {
      if (shouldSuppressMenuError(caughtError)) {
        return;
      }

      setError(true);
      showToastDynamic("Failed to load navigation menu.", "error");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!didInit.current) {
      didInit.current = true;
      void fetchSideMenu();
    }
  }, [fetchSideMenu]);

  return { menuItems, isLoading, error };
};
